import React, {useState, useEffect} from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import BackOfficeMenu from '../components/BackOfficeMenu';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import {usePermissions} from '../../utils/usePermissions';
import './EntreprisesManagement.css';

const API_URL = import.meta.env.VITE_API_URL;

const EntreprisesManagement = ({onNavigate, onLogout, currentPage}) => {
    const {hasPermission} = usePermissions();
    const [entreprises, setEntreprises] = useState([]);
    const [pendingCount, setPendingCount] = useState(0);
    const [filter, setFilter] = useState('all'); // 'all', 'active', 'pending'
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const userType = localStorage.getItem('typeUser');

        // Redirection pour les modérateurs et non-admins vers le profil front
        if (userType === 'moderateur' || userType !== 'admin') {
            onNavigate('profile');
            return;
        }

        fetchEntreprises();
    }, [onNavigate]);

    const fetchEntreprises = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API_URL}/entreprises`, {
                headers: {Authorization: `Bearer ${token}`}
            });
            setEntreprises(response.data);
            setPendingCount(response.data.filter(e => !e.isActive).length);
        } catch (error) {
            console.error('Erreur lors de la récupération des entreprises:', error);
            Swal.fire({
                icon: 'error',
                title: 'Erreur',
                text: 'Impossible de charger les entreprises'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleActivate = async (id, nomEntreprise) => {
        const result = await Swal.fire({
            title: 'Activer cette entreprise ?',
            html: `Voulez-vous activer le compte de <strong>${nomEntreprise}</strong> ?<br><small>Un email de confirmation sera envoyé.</small>`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#28a745',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Oui, activer',
            cancelButtonText: 'Annuler'
        });

        if (result.isConfirmed) {
            try {
                const token = localStorage.getItem('token');
                await axios.put(`${API_URL}/entreprises/${id}/activate`, {}, {
                    headers: {Authorization: `Bearer ${token}`}
                });

                Swal.fire({
                    icon: 'success',
                    title: 'Entreprise activée !',
                    text: 'Un email de confirmation a été envoyé.',
                    timer: 2000
                });

                fetchEntreprises();
            } catch (error) {
                console.error('Erreur lors de l\'activation:', error);
                Swal.fire({
                    icon: 'error',
                    title: 'Erreur',
                    text: error.response?.data?.message || 'Impossible d\'activer l\'entreprise'
                });
            }
        }
    };

    const handleDeactivate = async (id, nomEntreprise) => {
        const result = await Swal.fire({
            title: 'Désactiver cette entreprise ?',
            html: `Voulez-vous désactiver le compte de <strong>${nomEntreprise}</strong> ?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#ffc107',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Oui, désactiver',
            cancelButtonText: 'Annuler'
        });

        if (result.isConfirmed) {
            try {
                const token = localStorage.getItem('token');
                await axios.put(`${API_URL}/entreprises/${id}/deactivate`, {}, {
                    headers: {Authorization: `Bearer ${token}`}
                });

                Swal.fire({
                    icon: 'success',
                    title: 'Entreprise désactivée',
                    timer: 2000
                });

                fetchEntreprises();
            } catch (error) {
                console.error('Erreur lors de la désactivation:', error);
                Swal.fire({
                    icon: 'error',
                    title: 'Erreur',
                    text: error.response?.data?.message || 'Impossible de désactiver l\'entreprise'
                });
            }
        }
    };

    const handleDelete = async (id, nomEntreprise) => {
        const result = await Swal.fire({
            title: 'Supprimer cette entreprise ?',
            html: `Êtes-vous sûr de vouloir supprimer <strong>${nomEntreprise}</strong> ?<br><small class="text-danger">Cette action est irréversible.</small>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Oui, supprimer',
            cancelButtonText: 'Annuler'
        });

        if (result.isConfirmed) {
            try {
                const token = localStorage.getItem('token');
                await axios.delete(`${API_URL}/entreprises/${id}`, {
                    headers: {Authorization: `Bearer ${token}`}
                });

                Swal.fire({
                    icon: 'success',
                    title: 'Entreprise supprimée',
                    timer: 2000
                });

                fetchEntreprises();
            } catch (error) {
                console.error('Erreur lors de la suppression:', error);
                Swal.fire({
                    icon: 'error',
                    title: 'Erreur',
                    text: error.response?.data?.message || 'Impossible de supprimer l\'entreprise'
                });
            }
        }
    };

    const viewDetails = (entreprise) => {
        const dateInscription = new Date(entreprise.createdAt).toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });

        const dateActivationText = entreprise.dateActivation
            ? new Date(entreprise.dateActivation).toLocaleDateString('fr-FR', {
                day: '2-digit',
                month: 'long',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            })
            : 'Non activée';

        Swal.fire({
            title: `📋 ${entreprise.nomEntreprise}`,
            html: `
                <div style="text-align: left; padding: 1rem;">
                    <p><strong>Type:</strong> ${entreprise.typeEntreprise}</p>
                    <p><strong>Contact:</strong> ${entreprise.prenom} ${entreprise.nom}</p>
                    <p><strong>Email:</strong> ${entreprise.mail}</p>
                    <p><strong>Téléphone:</strong> ${entreprise.numeroTelephone}</p>
                    ${entreprise.adresse ? `<p><strong>Adresse:</strong> ${entreprise.adresse}</p>` : ''}
                    ${entreprise.ville ? `<p><strong>Ville:</strong> ${entreprise.ville}</p>` : ''}
                    ${entreprise.region ? `<p><strong>Région:</strong> ${entreprise.region}</p>` : ''}
                    <hr>
                    <p><strong>Statut:</strong> <span style="color: ${entreprise.isActive ? '#28a745' : '#ffc107'}">${entreprise.isActive ? '✓ Activée' : '⏳ En attente'}</span></p>
                    <p><strong>Date d'inscription:</strong> ${dateInscription}</p>
                    <p><strong>Date d'activation:</strong> ${dateActivationText}</p>
                </div>
            `,
            width: 600,
            confirmButtonText: 'Fermer'
        });
    };

    const filteredEntreprises = entreprises.filter(e => {
        if (filter === 'active') return e.isActive;
        if (filter === 'pending') return !e.isActive;
        return true;
    });

    return (
        <div className="backoffice-layout">
            <BackOfficeMenu
                onNavigate={onNavigate}
                onLogout={onLogout}
                currentPage={currentPage}
            />
            <div className="backoffice-content">
                <div className="entreprises-management">
                    <div className="dashboard-header">
                        <div>
                            <h1>Gestion des Entreprises</h1>
                            <p>Gérez les demandes de partenariat des entreprises</p>
                        </div>
                        {pendingCount > 0 && (
                            <div className="pending-badge">
                                {pendingCount} demande{pendingCount > 1 ? 's' : ''} en attente
                            </div>
                        )}
                    </div>

                    <div className="filters">
                        <button
                            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
                            onClick={() => setFilter('all')}
                        >
                            Toutes ({entreprises.length})
                        </button>
                        <button
                            className={`filter-btn ${filter === 'active' ? 'active' : ''}`}
                            onClick={() => setFilter('active')}
                        >
                            Activées ({entreprises.filter(e => e.isActive).length})
                        </button>
                        <button
                            className={`filter-btn ${filter === 'pending' ? 'active' : ''}`}
                            onClick={() => setFilter('pending')}
                        >
                            En attente ({pendingCount})
                        </button>
                    </div>

                    {loading ? (
                        <LoadingSpinner message="Chargement des entreprises..." />
                    ) : filteredEntreprises.length === 0 ? (
                        <div className="empty-state">
                            <p>Aucune entreprise trouvée</p>
                        </div>
                    ) : (
                        <div className="entreprises-grid">
                            {filteredEntreprises.map(entreprise => (
                                <div key={entreprise._id} className={`entreprise-card ${!entreprise.isActive ? 'pending' : ''}`}>
                                    <div className="card-header">
                                        <div>
                                            <h3>{entreprise.nomEntreprise}</h3>
                                        </div>
                                        <span className={`status-badge ${entreprise.isActive ? 'active' : 'pending'}`}>
                                            {entreprise.isActive ? '✓ Active' : '⏳ En attente'}
                                        </span>
                                    </div>

                                    <div className="card-body">
                                        <p><strong>Contact:</strong> {entreprise.prenom} {entreprise.nom}</p>
                                        <p><strong>Email:</strong> {entreprise.mail}</p>
                                        <p><strong>Tél:</strong> {entreprise.numeroTelephone}</p>
                                        {entreprise.ville && <p><strong>Ville:</strong> {entreprise.ville}</p>}
                                        <p className="date-inscription">
                                            Inscrit le {new Date(entreprise.createdAt).toLocaleDateString('fr-FR')}
                                        </p>
                                    </div>

                                    <div className="card-actions">
                                        <button
                                            className="btn-info"
                                            onClick={() => viewDetails(entreprise)}
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                <circle cx="12" cy="12" r="10"></circle>
                                                <line x1="12" y1="16" x2="12" y2="12"></line>
                                                <line x1="12" y1="8" x2="12.01" y2="8"></line>
                                            </svg>
                                            Détails
                                        </button>

                                        {entreprise.isActive ? (
                                            hasPermission('entreprises', 'update') && (
                                                <button
                                                    className="btn-warning"
                                                    onClick={() => handleDeactivate(entreprise._id, entreprise.nomEntreprise)}
                                                >
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <circle cx="12" cy="12" r="10"></circle>
                                                        <line x1="15" y1="9" x2="9" y2="15"></line>
                                                        <line x1="9" y1="9" x2="15" y2="15"></line>
                                                    </svg>
                                                    Désactiver
                                                </button>
                                            )
                                        ) : (
                                            hasPermission('entreprises', 'update') && (
                                                <button
                                                    className="btn-success"
                                                    onClick={() => handleActivate(entreprise._id, entreprise.nomEntreprise)}
                                                >
                                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                        <polyline points="20 6 9 17 4 12"></polyline>
                                                    </svg>
                                                    Activer
                                                </button>
                                            )
                                        )}

                                        {hasPermission('entreprises', 'delete') && (
                                            <button
                                                className="btn-danger"
                                                onClick={() => handleDelete(entreprise._id, entreprise.nomEntreprise)}
                                            >
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <polyline points="3 6 5 6 21 6"></polyline>
                                                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                                </svg>
                                                Supprimer
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default EntreprisesManagement;
